import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconAlertTriangle,
  IconCheck,
  IconDeviceFloppy,
  IconPencil,
  IconPlus,
  IconTrash,
  IconX,
} from "@tabler/icons-react";

import { api, ApiError } from "../lib/api";
import type {
  Estate,
  EstateTitleLinkRow,
  LandBalance,
  LandTitle,
} from "../lib/types";

interface Props {
  estate: Estate;
  onClose: () => void;
}

/**
 * Land titles + allocation manager for an estate.
 *
 * Because one estate may draw from multiple titles (and one title may be
 * split across multiple estates), this shows:
 *   - Current land-balance status (title cap vs block totals)
 *   - Linked titles with editable allocation
 *   - "Add existing title" (link an already-registered title)
 *   - "Create new title" (register + link in one action)
 *
 * Save is blocked at the API layer if block totals exceed title cap.
 * This modal surfaces that state so the user knows why blocks fail.
 */
export function EstateTitlesModal({ estate, onClose }: Props) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"link" | "create" | null>(null);

  const linksQ = useQuery({
    queryKey: ["estate-titles", estate.id],
    queryFn: () =>
      api.list<EstateTitleLinkRow>(`/v1/estates/${estate.id}/titles`),
  });
  const balanceQ = useQuery({
    queryKey: ["land-balance", estate.id],
    queryFn: () =>
      api.get<LandBalance>(`/v1/estates/${estate.id}/land-balance`),
  });
  const allTitlesQ = useQuery({
    queryKey: ["land-titles"],
    queryFn: () => api.list<LandTitle>("/v1/land-titles"),
  });

  const balance = balanceQ.data?.data;
  const linkedTitleIds = new Set(
    (linksQ.data?.data ?? []).map((r) => r.title.id),
  );
  const availableToLink = (allTitlesQ.data?.data ?? []).filter(
    (t) => !linkedTitleIds.has(t.id) && t.isActive,
  );

  const unlinkMut = useMutation({
    mutationFn: (linkId: string) => api.del(`/v1/estate-title-links/${linkId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["estate-titles", estate.id] });
      qc.invalidateQueries({ queryKey: ["land-balance", estate.id] });
    },
  });

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded border border-border bg-surface-2 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between">
          <div>
            <div className="text-sm font-medium">
              Land Titles · Estate {estate.code} · {estate.name}
            </div>
            <div className="text-[10px] text-text-secondary">
              1 estate ↔ many titles · one title can be split across estates
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-surface-1">×</button>
        </div>

        {/* Balance banner */}
        {balance && <BalanceBanner balance={balance} />}

        {/* Linked titles */}
        <div className="mt-4 rounded border border-border">
          <div className="flex items-center justify-between border-b border-border bg-surface-2 px-3 py-2">
            <div className="font-medium">
              Linked titles ({linksQ.data?.data?.length ?? 0})
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setMode("link")}
                disabled={!availableToLink.length}
                className="rounded border border-border px-2 py-1 text-xs hover:bg-surface-1 disabled:opacity-40"
              >
                <IconPlus size={12} className="mr-1 inline" />
                Link existing
              </button>
              <button
                onClick={() => setMode("create")}
                className="rounded bg-text-primary px-2 py-1 text-xs text-white hover:opacity-90"
              >
                <IconPlus size={12} className="mr-1 inline" />
                Create new
              </button>
            </div>
          </div>
          <table className="w-full text-xs">
            <thead className="border-b border-border bg-surface-1 text-text-secondary">
              <tr>
                <th className="p-2 text-left">Title #</th>
                <th className="p-2 text-right">Total area (ha)</th>
                <th className="p-2 text-right">Allocated to this estate</th>
                <th className="p-2 text-left">Owner</th>
                <th className="p-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(linksQ.data?.data ?? []).map((r) => (
                <TitleRow
                  key={r.link.id}
                  row={r}
                  estateId={estate.id}
                  onChanged={() => {
                    qc.invalidateQueries({ queryKey: ["estate-titles", estate.id] });
                    qc.invalidateQueries({ queryKey: ["land-balance", estate.id] });
                    qc.invalidateQueries({ queryKey: ["land-titles"] });
                  }}
                  onUnlink={() => {
                    if (confirm(`Unlink title ${r.title.titleNumber}?`)) {
                      unlinkMut.mutate(r.link.id);
                    }
                  }}
                />
              ))}
              {!(linksQ.data?.data?.length ?? 0) && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-text-secondary">
                    No titles linked yet. Blocks will save without a title cap
                    until you link at least one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Add / create title panels */}
        {mode === "link" && (
          <LinkExistingPanel
            estateId={estate.id}
            available={availableToLink}
            onDone={() => {
              setMode(null);
              qc.invalidateQueries({ queryKey: ["estate-titles", estate.id] });
              qc.invalidateQueries({ queryKey: ["land-balance", estate.id] });
            }}
            onCancel={() => setMode(null)}
          />
        )}
        {mode === "create" && (
          <CreateTitlePanel
            estateId={estate.id}
            onDone={() => {
              setMode(null);
              qc.invalidateQueries({ queryKey: ["estate-titles", estate.id] });
              qc.invalidateQueries({ queryKey: ["land-titles"] });
              qc.invalidateQueries({ queryKey: ["land-balance", estate.id] });
            }}
            onCancel={() => setMode(null)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Editable row: user can adjust the title's total_area_ha (updates the
 * land_title row) and the link's allocated_area_ha (updates the
 * estate_title_link row). Both PATCH endpoints already exist.
 * Server rejects overflow (sum of allocations > title total).
 */
function TitleRow({
  row,
  onChanged,
  onUnlink,
}: {
  row: EstateTitleLinkRow;
  estateId: string;
  onChanged: () => void;
  onUnlink: () => void;
}) {
  const [editing, setEditing] = useState<"total" | "alloc" | null>(null);
  const [totalDraft, setTotalDraft] = useState(row.title.totalAreaHa);
  const [allocDraft, setAllocDraft] = useState(row.link.allocatedAreaHa);
  const [error, setError] = useState<string | null>(null);

  const patchTitle = useMutation({
    mutationFn: () =>
      api.patch(`/v1/land-titles/${row.title.id}`, {
        totalAreaHa: totalDraft,
      }),
    onSuccess: () => {
      setEditing(null);
      setError(null);
      onChanged();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : String(err));
    },
  });

  const patchLink = useMutation({
    mutationFn: () =>
      api.patch(`/v1/estate-title-links/${row.link.id}`, {
        allocatedAreaHa: allocDraft,
      }),
    onSuccess: () => {
      setEditing(null);
      setError(null);
      onChanged();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : String(err));
    },
  });

  return (
    <>
      <tr className="border-b border-border">
        <td className="p-2 font-mono">{row.title.titleNumber}</td>

        {/* Total area — editable */}
        <td className="p-2 text-right tabular-nums">
          {editing === "total" ? (
            <div className="flex items-center justify-end gap-1">
              <input
                type="number"
                step="0.01"
                value={totalDraft}
                onChange={(e) => setTotalDraft(e.target.value)}
                className="w-20 rounded border border-border bg-surface-2 px-1 py-0.5 text-right font-mono text-xs"
              />
              <button
                onClick={() => patchTitle.mutate()}
                disabled={patchTitle.isPending}
                className="rounded bg-emerald-600 p-1 text-white hover:bg-emerald-700 disabled:opacity-50"
                title="Save"
              >
                <IconDeviceFloppy size={12} />
              </button>
              <button
                onClick={() => {
                  setEditing(null);
                  setTotalDraft(row.title.totalAreaHa);
                }}
                className="rounded bg-slate-600 p-1 text-white hover:bg-slate-700"
                title="Cancel"
              >
                <IconX size={12} />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-1">
              <span>{Number(row.title.totalAreaHa).toFixed(2)}</span>
              <button
                onClick={() => setEditing("total")}
                className="rounded p-1 text-text-secondary hover:bg-surface-1 hover:text-text-primary"
                title="Edit total title area"
              >
                <IconPencil size={11} />
              </button>
            </div>
          )}
        </td>

        {/* Allocated — editable */}
        <td className="p-2 text-right tabular-nums font-medium">
          {editing === "alloc" ? (
            <div className="flex items-center justify-end gap-1">
              <input
                type="number"
                step="0.01"
                value={allocDraft}
                onChange={(e) => setAllocDraft(e.target.value)}
                className="w-20 rounded border border-border bg-surface-2 px-1 py-0.5 text-right font-mono text-xs"
              />
              <button
                onClick={() => patchLink.mutate()}
                disabled={patchLink.isPending}
                className="rounded bg-emerald-600 p-1 text-white hover:bg-emerald-700 disabled:opacity-50"
                title="Save"
              >
                <IconDeviceFloppy size={12} />
              </button>
              <button
                onClick={() => {
                  setEditing(null);
                  setAllocDraft(row.link.allocatedAreaHa);
                }}
                className="rounded bg-slate-600 p-1 text-white hover:bg-slate-700"
                title="Cancel"
              >
                <IconX size={12} />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-1">
              <span>{Number(row.link.allocatedAreaHa).toFixed(2)}</span>
              <button
                onClick={() => setEditing("alloc")}
                className="rounded p-1 text-text-secondary hover:bg-surface-1 hover:text-text-primary"
                title="Edit allocation to this estate"
              >
                <IconPencil size={11} />
              </button>
            </div>
          )}
        </td>

        <td className="p-2">{row.title.ownerName ?? "—"}</td>
        <td className="p-2 text-center">
          <button
            onClick={onUnlink}
            className="rounded bg-slate-800 px-2 py-1 text-white hover:bg-red-700"
            title="Unlink title from estate"
          >
            <IconTrash size={12} />
          </button>
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={5} className="bg-red-50 px-2 py-1 text-[10px] text-red-700">
            {error}
          </td>
        </tr>
      )}
    </>
  );
}

function BalanceBanner({ balance }: { balance: LandBalance }) {
  const overflow = balance.status === "OVERFLOW";
  const under = balance.status === "UNDERSHOOT";
  const bg = overflow
    ? "border-red-300 bg-red-50 text-red-900"
    : under
      ? "border-amber-300 bg-amber-50 text-amber-900"
      : "border-emerald-300 bg-emerald-50 text-emerald-900";
  const Icon = overflow || under ? IconAlertTriangle : IconCheck;
  return (
    <div className={`flex items-start gap-2 rounded border p-3 text-xs ${bg}`}>
      <Icon size={14} className="mt-0.5" />
      <div className="flex-1">
        <div className="font-medium">
          {overflow && "Blocks OVERFLOW title cap"}
          {under && "Blocks under-cover title"}
          {balance.status === "BALANCED" && "Land balance OK"}
        </div>
        <div className="mt-1 grid grid-cols-4 gap-2 text-[11px]">
          <div>
            Title cap · <strong>{balance.titleAreaHa.toFixed(2)}</strong> ha
          </div>
          <div>
            Block total · <strong>{balance.blockAreaHa.toFixed(2)}</strong> ha
          </div>
          <div>
            Δ · <strong>{balance.delta.toFixed(2)}</strong> ha
          </div>
          <div>
            Tolerance · ±<strong>{balance.tolerance.toFixed(2)}</strong>
          </div>
        </div>
        {overflow && (
          <div className="mt-1">
            Fix by (a) reducing block ha, (b) linking more titles, or (c)
            increasing an allocation. New block saves are BLOCKED until this
            is resolved.
          </div>
        )}
      </div>
    </div>
  );
}

function LinkExistingPanel({
  estateId,
  available,
  onDone,
  onCancel,
}: {
  estateId: string;
  available: LandTitle[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [titleId, setTitleId] = useState("");
  const [allocated, setAllocated] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/v1/land-titles/${titleId}/estates`, {
        estateId,
        allocatedAreaHa: allocated,
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 rounded border border-border bg-surface-2 p-3">
      <div className="mb-2 text-xs font-medium">Link existing title</div>
      <div className="grid gap-2 sm:grid-cols-3">
        <select
          value={titleId}
          onChange={(e) => setTitleId(e.target.value)}
          className="input"
        >
          <option value="">— pick title —</option>
          {available.map((t) => (
            <option key={t.id} value={t.id}>
              {t.titleNumber} · {Number(t.totalAreaHa).toFixed(2)} ha
            </option>
          ))}
        </select>
        <input
          type="number"
          step="0.01"
          value={allocated}
          onChange={(e) => setAllocated(e.target.value)}
          placeholder="Allocated ha"
          className="input"
        />
        <div className="flex gap-2">
          <button
            onClick={submit}
            disabled={!titleId || !allocated || saving}
            className="flex-1 rounded bg-text-primary px-3 py-1 text-xs text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Linking…" : "Link"}
          </button>
          <button
            onClick={onCancel}
            className="rounded border border-border px-3 py-1 text-xs hover:bg-surface-1"
          >
            Cancel
          </button>
        </div>
      </div>
      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
    </div>
  );
}

function CreateTitlePanel({
  estateId,
  onDone,
  onCancel,
}: {
  estateId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [titleNumber, setTitleNumber] = useState("");
  const [totalAreaHa, setTotalAreaHa] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [allocated, setAllocated] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await api.post<LandTitle>("/v1/land-titles", {
        titleNumber,
        totalAreaHa,
        ownerName: ownerName || undefined,
      });
      await api.post(`/v1/land-titles/${created.data.id}/estates`, {
        estateId,
        allocatedAreaHa: allocated || totalAreaHa,
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 rounded border border-border bg-surface-2 p-3">
      <div className="mb-2 text-xs font-medium">Create new title + link</div>
      <div className="grid gap-2 sm:grid-cols-4">
        <input
          type="text"
          value={titleNumber}
          onChange={(e) => setTitleNumber(e.target.value)}
          placeholder="Title # (e.g. HSD 12345)"
          className="input"
        />
        <input
          type="number"
          step="0.01"
          value={totalAreaHa}
          onChange={(e) => setTotalAreaHa(e.target.value)}
          placeholder="Total ha"
          className="input"
        />
        <input
          type="text"
          value={ownerName}
          onChange={(e) => setOwnerName(e.target.value)}
          placeholder="Owner (optional)"
          className="input"
        />
        <input
          type="number"
          step="0.01"
          value={allocated}
          onChange={(e) => setAllocated(e.target.value)}
          placeholder="Allocate to this estate (defaults to total)"
          className="input"
        />
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="rounded border border-border px-3 py-1 text-xs hover:bg-surface-1"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={!titleNumber || !totalAreaHa || saving}
          className="rounded bg-text-primary px-3 py-1 text-xs text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Create + link"}
        </button>
      </div>
      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
    </div>
  );
}
